/**
 * api.ts — Cliente de datos PORTABLE del frontend (ADR 0001 / 0004).
 *
 * Regla de la casa: el frontend NUNCA importa @supabase/supabase-js.
 * Este módulo habla con las APIs REST estándar
 * (PostgREST / GoTrue / Storage) con `fetch` puro, de modo que migrar de
 * Supabase a Azure sólo cambia la implementación de estas funciones.
 *
 * Seguridad:
 *  - Toda consulta viaja con el JWT del usuario → RLS acota al tenant.
 *  - El hash SHA-256 de cada documento se calcula EN EL NAVEGADOR antes de
 *    subirlo (crypto.subtle) y se registra en `documento` — la huella de
 *    integridad nace con el archivo, no después.
 *  - Tras la subida, el objeto se descarga y re-hashea para verificar que lo
 *    que aterrizó en Storage coincide con el original ANTES de registrar la
 *    fila (issue #44). Si el INSERT falla, el objeto queda huérfano y se emite
 *    una alerta visible; el script scripts/reconcile-boveda.ts lo detecta.
 */

import type { Rol } from "../auth/authContext.ts";
import type { Tables } from "../../../supabase/types.ts";

// ─────────────────────────────────────────────────────────────────────────────
// Configuración (Vite env)
// ─────────────────────────────────────────────────────────────────────────────

const URL_BASE: string = import.meta.env.VITE_SUPABASE_URL ?? "";
const ANON_KEY: string = import.meta.env.VITE_SUPABASE_ANON_KEY ?? "";
const TOTP_ISSUER = "Argus AML";
const TOTP_FRIENDLY_NAME = "Argus MFA";
const MS_POR_SEGUNDO_AUTH = 1000;
const REQUEST_TIMEOUT_MS = 30_000;
const HTTP_REQUEST_TIMEOUT = 408;

/** ¿Hay backend real configurado? (CI compila con placeholders). */
export function apiConfigurada(): boolean {
  return URL_BASE.length > 0 && !URL_BASE.includes("placeholder") && ANON_KEY.length > 0 && ANON_KEY !== "placeholder";
}

export class ApiError extends Error {
  constructor(mensaje: string, readonly status?: number, readonly code?: string) {
    super(mensaje);
    this.name = "ApiError";
  }
}

/**
 * Código del 409 que devuelven generar-xml-inmuebles/-arrendamiento cuando un
 * aviso de PM/fideicomiso saldría sin beneficiario controlador (issue BC-3). La
 * UI lo reconoce para abrir el diálogo de confirmación explícita del REC.
 */
export const CODIGO_BC_AUSENTE = "bc_ausente_requiere_confirmacion";

// ─────────────────────────────────────────────────────────────────────────────
// HTTP base
// ─────────────────────────────────────────────────────────────────────────────

interface ReqOpts {
  method?: string;
  token?: string;
  body?: unknown;
  headers?: Record<string, string>;
  timeoutMs?: number;
  /** Cuerpo binario (Storage): se envía tal cual, sin JSON. */
  raw?: Blob;
}

async function req(path: string, opts: ReqOpts = {}): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    controller.abort();
  }, opts.timeoutMs ?? REQUEST_TIMEOUT_MS);
  const headers: Record<string, string> = {
    apikey: ANON_KEY,
    ...(opts.token ? { Authorization: `Bearer ${opts.token}` } : {}),
    ...(opts.raw ? {} : { "Content-Type": "application/json" }),
    ...(opts.headers ?? {}),
  };
  try {
    const res = await fetch(`${URL_BASE}${path}`, {
      method: opts.method ?? (opts.body !== undefined || opts.raw ? "POST" : "GET"),
      headers,
      body: opts.raw ?? (opts.body !== undefined ? JSON.stringify(opts.body) : null),
      signal: controller.signal,
    });
    if (!res.ok) {
      let detalle = res.statusText;
      let code: string | undefined;
      try {
        const cuerpo: unknown = await res.json();
        if (cuerpo && typeof cuerpo === "object") {
          const c = cuerpo as Record<string, unknown>;
          const candidato = [c["message"], c["error_description"], c["error"], c["msg"]]
            .find((x): x is string => typeof x === "string");
          if (candidato) detalle = candidato;
          if (typeof c["code"] === "string") code = c["code"];
        }
      } catch {
        /* cuerpo no-JSON: conservar statusText */
      }
      throw new ApiError(detalle, res.status, code);
    }
    return res;
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") {
      throw new ApiError("La solicitud tardó demasiado. Revisa la conexión e intenta de nuevo.", HTTP_REQUEST_TIMEOUT);
    }
    throw e;
  } finally {
    clearTimeout(timeoutId);
  }
}

async function json<T>(res: Response): Promise<T> {
  const data: unknown = await res.json();
  return data as T;
}

export const __testing = { req };

// ─────────────────────────────────────────────────────────────────────────────
// Autenticación (GoTrue password grant + MFA TOTP opcional) + resolución de rol
// ─────────────────────────────────────────────────────────────────────────────
// MFA/TOTP es recomendado, no obligatorio. El password grant produce un JWT aal1
// y ya permite operar dentro del tenant porque RLS depende de tenant_id, no de
// aal2. Si el usuario quiere elevar la sesion, puede verificar un factor TOTP
// existente (loginPasoMfa) o enrolar uno con QR (enrolarTotp).

export interface ApiSession {
  accessToken: string;
  refreshToken?: string;
  expiresAt?: number;
  userId: string;
  tenantId: string;
  rol: Rol;
  email: string;
  /** aal2 = ya pasó el challenge TOTP; aal1 = sólo password. */
  aal: "aal1" | "aal2";
}

/**
 * Resultado de iniciarSesion. MFA no bloquea el acceso: cuando hay una
 * recomendacion pendiente, se devuelve tambien la sesion usable para que el
 * usuario pueda omitir ese paso.
 */
export type ResultadoLogin =
  /** No hay recomendacion accionable de MFA; entrar directo. */
  | { estado: "ok"; session: ApiSession }
  /** Tiene factor TOTP: puede verificarlo ahora, o continuar con aal1. */
  | { estado: "recomienda_mfa"; session: ApiSession; accessToken: string; factorId: string }
  /** No tiene factor TOTP verificado: se recomienda enrolarlo con QR. */
  | { estado: "recomienda_enrolamiento"; session: ApiSession; accessToken: string };

interface TokenResponse {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
  user?: { id?: string; email?: string };
}

interface FactorGoTrue {
  id: string;
  factor_type: string;
  status: "verified" | "unverified";
}

interface UsuarioGoTrue {
  id?: string;
  email?: string;
  factors?: FactorGoTrue[];
}

/** Decodifica el payload de un JWT sin verificar (la verificación es del servidor). */
function jwtPayload(token: string): Record<string, unknown> {
  const parte = token.split(".")[1];
  if (!parte) return {};
  try {
    const data: unknown = JSON.parse(atob(parte.replace(/-/g, "+").replace(/_/g, "/")));
    return (data && typeof data === "object" ? data : {}) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function aalDelToken(token: string): "aal1" | "aal2" {
  const claims = jwtPayload(token);
  return claims["aal"] === "aal2" ? "aal2" : "aal1";
}

/** Arma la ApiSession final a partir del access_token vigente. */
function expiracionToken(token: string, expiresIn?: number): number | undefined {
  const claims = jwtPayload(token);
  const exp = claims["exp"];
  if (typeof exp === "number") return exp * MS_POR_SEGUNDO_AUTH;
  if (typeof expiresIn === "number") return Date.now() + expiresIn * MS_POR_SEGUNDO_AUTH;
  return undefined;
}

function sesionDesdeToken(token: string, email: string, refreshToken?: string, expiresIn?: number): ApiSession {
  const claims = jwtPayload(token);
  const appMeta = (claims["app_metadata"] ?? {}) as Record<string, unknown>;
  const metaTenant = appMeta["tenant_id"];
  const tenantId = typeof metaTenant === "string" ? metaTenant : "";
  const sub = claims["sub"];
  const userId = typeof sub === "string" ? sub : "";
  if (!tenantId) {
    throw new ApiError("La cuenta no tiene tenant asignado (app_metadata.tenant_id). Contacta al administrador.");
  }
  const apiSession: ApiSession = {
    accessToken: token,
    userId,
    tenantId,
    rol: "rec",
    email,
    aal: aalDelToken(token),
  };
  if (refreshToken) apiSession.refreshToken = refreshToken;
  const expiresAt = expiracionToken(token, expiresIn);
  if (expiresAt) apiSession.expiresAt = expiresAt;
  return apiSession;
}

/** Resuelve el rol real desde la tabla `usuario` (RLS: el propio tenant puede leerla). */
async function resolverRol(session: ApiSession): Promise<ApiSession> {
  const filas = await json<{ rol: Rol }[]>(
    await req(`/rest/v1/usuario?auth_uid=eq.${session.userId}&select=rol`, { token: session.accessToken }),
  );
  if (!filas[0]) {
    throw new ApiError("No se encontró la fila de usuario para resolver tu rol. La app no asumirá rol REC por defecto.");
  }
  return { ...session, rol: filas[0].rol };
}

/**
 * Password grant contra GoTrue. Devuelve una ApiSession usable y, cuando aplica,
 * una recomendacion no bloqueante para verificar o enrolar TOTP.
 */
export async function iniciarSesion(email: string, password: string): Promise<ResultadoLogin> {
  const res = await req("/auth/v1/token?grant_type=password", { body: { email, password } });
  const tok = await json<TokenResponse>(res);
  const session = await resolverRol(sesionDesdeToken(tok.access_token, email, tok.refresh_token, tok.expires_in));
  try {
    const usuario = await json<UsuarioGoTrue>(await req("/auth/v1/user", { token: tok.access_token }));
    const factorVerificado = (usuario.factors ?? []).find((f) => f.factor_type === "totp" && f.status === "verified");
    if (factorVerificado) {
      return { estado: "recomienda_mfa", session, accessToken: tok.access_token, factorId: factorVerificado.id };
    }
    return { estado: "recomienda_enrolamiento", session, accessToken: tok.access_token };
  } catch {
    // La recomendacion de MFA no debe impedir el login si GoTrue no devuelve factores.
    return { estado: "ok", session };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// MFA TOTP — enrolamiento (usuario sin factor) y verificación (login)
// ─────────────────────────────────────────────────────────────────────────────

export interface EnrolamientoTotp {
  factorId: string;
  /** URI otpauth:// para pintar el QR en el cliente (no se manda a servidor). */
  uri: string;
  /** SVG o data URL del QR devuelto por GoTrue, cuando esta disponible. */
  qrCode?: string;
  /** Secreto en texto, por si el usuario prefiere capturarlo a mano. */
  secreto: string;
}

interface EnrollResponse {
  id: string;
  totp?: { qr_code: string; secret: string; uri: string };
}

interface ChallengeResponse {
  id: string;
}

/** Da de alta un factor TOTP nuevo. El usuario escanea el QR y confirma con verificarEnrolamientoTotp. */
export async function enrolarTotp(accessToken: string): Promise<EnrolamientoTotp> {
  const res = await req("/auth/v1/factors", {
    token: accessToken,
    body: {
      factor_type: "totp",
      friendly_name: TOTP_FRIENDLY_NAME,
      issuer: TOTP_ISSUER,
    },
  });
  const data = await json<EnrollResponse>(res);
  if (!data.totp) throw new ApiError("GoTrue no devolvió datos TOTP para el enrolamiento.");
  return { factorId: data.id, uri: data.totp.uri, qrCode: data.totp.qr_code, secreto: data.totp.secret };
}

/** Confirma el enrolamiento con el primer código TOTP. Éxito → sesión con AAL actualizado. */
export async function verificarEnrolamientoTotp(
  accessToken: string,
  factorId: string,
  codigo: string,
  email: string,
): Promise<ApiSession> {
  const challenge = await json<ChallengeResponse>(
    await req(`/auth/v1/factors/${factorId}/challenge`, { token: accessToken }),
  );
  const tok = await json<TokenResponse>(
    await req(`/auth/v1/factors/${factorId}/verify`, {
      token: accessToken,
      body: { challenge_id: challenge.id, code: codigo },
    }),
  );
  return resolverRol(sesionDesdeToken(tok.access_token, email, tok.refresh_token, tok.expires_in));
}

/** Resuelve el challenge TOTP en login (usuario que ya tenía factor enrolado). */
export async function loginPasoMfa(
  accessToken: string,
  factorId: string,
  codigo: string,
  email: string,
): Promise<ApiSession> {
  const challenge = await json<ChallengeResponse>(
    await req(`/auth/v1/factors/${factorId}/challenge`, { token: accessToken }),
  );
  const tok = await json<TokenResponse>(
    await req(`/auth/v1/factors/${factorId}/verify`, {
      token: accessToken,
      body: { challenge_id: challenge.id, code: codigo },
    }),
  );
  return resolverRol(sesionDesdeToken(tok.access_token, email, tok.refresh_token, tok.expires_in));
}

/** Renueva access_token antes de expirar; si falla, AuthProvider limpia sesión. */
export async function refrescarSesion(refreshToken: string, email: string): Promise<ApiSession> {
  const tok = await json<TokenResponse>(
    await req("/auth/v1/token?grant_type=refresh_token", { body: { refresh_token: refreshToken } }),
  );
  const emailVigente = tok.user?.email ?? email;
  return resolverRol(sesionDesdeToken(
    tok.access_token,
    emailVigente,
    tok.refresh_token ?? refreshToken,
    tok.expires_in,
  ));
}

// ─────────────────────────────────────────────────────────────────────────────
// Personas (cliente_aportante)
// ─────────────────────────────────────────────────────────────────────────────

export type TipoPersona = "pf" | "pm" | "fideicomiso";

/**
 * Fila de `cliente_aportante` derivada del esquema real (issue #42).
 * Drifts corregidos respecto a la interfaz manual anterior:
 *  - `tipo`: era `TipoPersona | "otra"` (unión ad hoc); la columna real es el
 *    enum `tipo_cliente`, cuyos 4 valores ("pf"|"pm"|"fideicomiso"|"otra")
 *    coinciden — se usa el enum generado en vez de reconstruirlo a mano.
 *  - `pais_nacionalidad`: la interfaz manual la marcaba `string | null`; la
 *    columna real es NOT NULL con default (`string` a secas). Un valor NULL
 *    aquí nunca ha podido pasar el INSERT — el `| null` era optimismo, no
 *    reflejo del esquema.
 * `domicilio`/`contacto`/`representante` se sobreescriben a
 * `Record<string, unknown>` (en vez del `Json` generado): el código
 * consumidor (PersonasView.tsx) indexa estos campos con literales de string
 * (`dom["calle"]`, `con["telefono"]`), lo cual `Json` no permite de forma
 * segura porque su unión también admite las variantes primitivas/array
 * (mismo patrón que TemporizadorRow.metadatos más abajo).
 */
export type PersonaRow = Pick<
  Tables<"cliente_aportante">,
  | "id"
  | "canonical_id"
  | "tipo"
  | "nombre"
  | "apellido_paterno"
  | "apellido_materno"
  | "fecha_nacimiento"
  | "rfc"
  | "curp"
  | "pais_nacionalidad"
  | "actividad_economica"
  | "denominacion_razon"
  | "fecha_constitucion"
  | "giro_mercantil"
  | "fiduciario_denominacion"
  | "fideicomiso_identificador"
  | "estatus_pep"
  | "relacion_pep"
  | "pep_nombre"
  | "pep_cargo"
  | "pep_vinculo"
  | "pep_grado_parentesco"
  | "nivel_riesgo"
  | "expediente_completo"
  | "created_at"
> & {
  domicilio: Record<string, unknown>;
  contacto: Record<string, unknown>;
  representante: Record<string, unknown> | null;
};

/**
 * Relación del cliente con el régimen PEP (enum `tipo_relacion_pep`, migración
 * 0066). Es la FUENTE DE VERDAD de la diligencia reforzada por PEP: los tres
 * valores distintos de `ninguna` (el cliente ES una PEP, o es FAMILIAR/ALLEGADO
 * de una) activan el caso de aprobación de dirección y los requisitos DDR. El
 * flag legado `estatus_pep` es sólo el reflejo de `es_pep` y lo deriva un trigger
 * de reconciliación en el backend, así que el frontend escribe `relacion_pep`, NO
 * `estatus_pep` (ver `normalizarRelacionPep`).
 */
export type RelacionPep = PersonaRow["relacion_pep"];

/**
 * Campos de captura de la relación con PEP que el frontend escribe como fuente de
 * verdad (0066). Al pasar a `ninguna`, el backend limpia los `pep_*` y anula por
 * corrección el caso de aprobación PEP abierto; aquí igual se mandan en `null`
 * para dejar el intent explícito y no depender sólo del trigger.
 */
export interface CamposRelacionPep {
  relacion_pep: RelacionPep;
  pep_nombre: string | null;
  pep_cargo: string | null;
  pep_vinculo: string | null;
  pep_grado_parentesco: number | null;
}

const PEP_GRADO_MIN = 1;
const PEP_GRADO_MAX = 4;

/**
 * Normaliza la captura de la relación con PEP a las columnas del backend,
 * imponiendo del lado del cliente las mismas invariantes que los CHECK de 0066:
 *  - `ninguna` → todos los `pep_*` en null (satisface `ck_relacion_pep_datos`).
 *  - `es_pep`  → `pep_nombre` null (el PEP es el propio cliente; su nombre ya
 *    vive en la persona) y sin vínculo/grado.
 *  - grado sólo aplica a `familiar_pep` con vínculo por parentesco
 *    (consanguinidad/afinidad) y acotado a 1..4 (`ck_pep_grado_parentesco_rango`).
 * NO produce `estatus_pep`: lo deriva el trigger `trg_pep_reconciliar`.
 */
export function normalizarRelacionPep(entrada: {
  relacion: RelacionPep;
  nombre?: string | null;
  cargo?: string | null;
  vinculo?: string | null;
  grado?: number | string | null;
}): CamposRelacionPep {
  if (entrada.relacion === "ninguna") {
    return { relacion_pep: "ninguna", pep_nombre: null, pep_cargo: null, pep_vinculo: null, pep_grado_parentesco: null };
  }
  const limpiar = (v?: string | null): string | null => {
    const t = (v ?? "").trim();
    return t === "" ? null : t;
  };
  const esPropio = entrada.relacion === "es_pep";
  const vinculo = esPropio ? null : limpiar(entrada.vinculo);
  let grado: number | null = null;
  if (entrada.relacion === "familiar_pep" && (vinculo === "consanguinidad" || vinculo === "afinidad")) {
    const n = typeof entrada.grado === "number" ? entrada.grado : Number(entrada.grado);
    grado = Number.isFinite(n) && n >= PEP_GRADO_MIN && n <= PEP_GRADO_MAX ? n : null;
  }
  return {
    relacion_pep: entrada.relacion,
    pep_nombre: esPropio ? null : limpiar(entrada.nombre),
    pep_cargo: limpiar(entrada.cargo),
    pep_vinculo: vinculo,
    pep_grado_parentesco: grado,
  };
}

const PERSONA_COLS =
  "id,canonical_id,tipo,nombre,apellido_paterno,apellido_materno,fecha_nacimiento,rfc,curp,pais_nacionalidad," +
  "actividad_economica,denominacion_razon,fecha_constitucion,giro_mercantil,fiduciario_denominacion," +
  "fideicomiso_identificador,estatus_pep,relacion_pep,pep_nombre,pep_cargo,pep_vinculo,pep_grado_parentesco," +
  "nivel_riesgo,expediente_completo,domicilio,contacto,representante,created_at";

export async function listarPersonas(token: string): Promise<PersonaRow[]> {
  return json<PersonaRow[]>(
    await req(`/rest/v1/cliente_aportante?select=${PERSONA_COLS}&order=created_at.desc`, { token }),
  );
}

export interface CatalogoRow {
  clave: string;
  nombre: string;
  soportado?: boolean;
  tipo_persona?: "pf" | "pm";
}

export type CatalogoTabla =
  | "cat_instrumento_monetario"
  | "cat_moneda"
  | "cat_tipo_alerta"
  | "cat_inm_tipo_inmueble"
  | "cat_inm_forma_pago"
  | "cat_inm_figura_cliente"
  | "cat_inm_figura_so"
  | "cat_inm_entidad_federativa"
  | "cat_inm_tipo_alerta"
  | "cat_ari_tipo_alerta"
  | "cat_actividad_giro_pld";

export async function listarCatalogo(token: string, tabla: CatalogoTabla, query = ""): Promise<CatalogoRow[]> {
  const select = tabla === "cat_inm_figura_so"
    ? "clave,nombre,soportado"
    : tabla === "cat_actividad_giro_pld"
      ? "tipo_persona,clave,nombre"
      : "clave,nombre";
  return json<CatalogoRow[]>(
    await req(`/rest/v1/${tabla}?select=${select}${query}&order=clave.asc`, { token }),
  );
}

async function crearEntidad<T>(args: {
  token: string;
  tenantId: string;
  tabla: string;
  select: string;
  datos: Record<string, unknown>;
  mensajeVacio: string;
}): Promise<T> {
  const filas = await json<T[]>(
    await req(`/rest/v1/${args.tabla}?select=${args.select}`, {
      token: args.token,
      body: { tenant_id: args.tenantId, ...args.datos },
      headers: { Prefer: "return=representation" },
    }),
  );
  const fila = filas[0];
  if (!fila) throw new ApiError(args.mensajeVacio);
  return fila;
}

/** Alta de persona. `datos` lleva sólo las columnas del tipo (nulls permitidos). */
export async function crearPersona(
  token: string,
  tenantId: string,
  datos: Record<string, unknown>,
): Promise<PersonaRow> {
  return crearEntidad<PersonaRow>({
    token,
    tenantId,
    tabla: "cliente_aportante",
    select: PERSONA_COLS,
    datos,
    mensajeVacio: "El alta no devolvió la fila creada.",
  });
}

export async function actualizarPersona(
  token: string,
  id: string,
  patch: Record<string, unknown>,
): Promise<void> {
  await req(`/rest/v1/cliente_aportante?id=eq.${id}`, { method: "PATCH", token, body: patch });
}

// ─────────────────────────────────────────────────────────────────────────────
// Beneficiario controlador (BC) — captura del grafo de control (issues BC-1/BC-2)
// ─────────────────────────────────────────────────────────────────────────────
// El grafo BC se persiste en bc_nodo/bc_arista (migración 0009) y se lee/escribe
// vía las RPC transaccionales bc_obtener_grafo / bc_guardar_grafo (0056). Los
// tipos se derivan del esquema real (Tables<>); la participación vive dentro de un
// jsonb (RPC), así que llega como número JSON, no como el string que PostgREST usa
// para columnas numeric de primer nivel.

/** Vía de control de una arista (enum tipo_control_bc de 0009). */
export type TipoControlBc = Tables<"bc_arista">["tipo_control"];

/**
 * Nodo del grafo BC (PF/PM/fideicomiso de la cadena de control).
 *
 * PEP por nodo (migración 0067): cada nodo **persona física** puede llevar su
 * relación con PEP (`relacion_pep` + `pep_*`, los mismos campos que
 * `cliente_aportante` en 0066). Van **opcionales**: los nodos PM/fideicomiso no
 * los llevan (el CHECK del backend lo prohíbe) y un nodo recién construido puede
 * omitirlos (el backend asume `ninguna`). `bc_guardar_grafo` los persiste;
 * `bc_obtener_grafo` los hidrata. El estatus PEP de la EMPRESA no vive aquí: lo
 * deriva el backend a `cliente_aportante.relacion_pep = 'controlada_por_pep'`.
 */
export type BcNodo = Pick<
  Tables<"bc_nodo">,
  | "id"
  | "tipo"
  | "es_raiz"
  | "nombre"
  | "denominacion"
  | "rfc"
  | "curp"
  | "pais_nacionalidad"
  | "apellido_paterno"
  | "apellido_materno"
  | "fecha_nacimiento"
> &
  Partial<
    Pick<
      Tables<"bc_nodo">,
      "relacion_pep" | "pep_nombre" | "pep_cargo" | "pep_vinculo" | "pep_grado_parentesco"
    >
  >;

/** Arista del grafo BC: `origen` controla `participacion`% de `destino`. */
export type BcArista = Pick<
  Tables<"bc_arista">,
  "id" | "origen_nodo_id" | "destino_nodo_id" | "participacion" | "tipo_control"
>;

/** Subgrafo devuelto por bc_obtener_grafo para hidratar el editor. */
export interface BcGrafo {
  nodos: BcNodo[];
  aristas: BcArista[];
}

/** Beneficiario controlador efectivo (PF >25% o control por otros medios). */
export interface BcBeneficiario {
  nodo_id: string;
  nombre: string | null;
  rfc: string | null;
  participacion_efectiva: number;
  por_otros_medios: boolean;
}

/**
 * Persona física del grafo bajo régimen PEP (relacion_pep <> 'ninguna'), del
 * resultado de bc_guardar_grafo (migración 0067). Alimenta el "N de M personas
 * de la estructura son PEP" del apartado derivado de la empresa.
 */
export interface BcPersonaPep {
  nodo_id: string;
  nombre: string | null;
  relacion_pep: RelacionPep;
}

/** Resultado de bc_guardar_grafo: conteos + beneficiarios recalculados. */
export interface BcGuardarResultado {
  ok: boolean;
  nodos: number;
  aristas: number;
  beneficiarios: BcBeneficiario[];
  /**
   * Personas del grafo bajo régimen PEP (0067). Opcional en el tipo por robustez
   * (backend legado podría no incluirlo); el frontend además lo deriva de los
   * nodos hidratados, que son la fuente de verdad por-nodo.
   */
  personas_pep?: BcPersonaPep[];
}

/** Lee el subgrafo BC de una persona (por canonical_id) para hidratar el editor. */
export async function obtenerGrafoBc(token: string, personaCanonical: string): Promise<BcGrafo> {
  return json<BcGrafo>(
    await req("/rest/v1/rpc/bc_obtener_grafo", {
      token,
      body: { p_persona_canonical: personaCanonical },
    }),
  );
}

/**
 * Guarda (reemplaza atómicamente) el subgrafo BC de una persona y devuelve los
 * beneficiarios controladores recalculados. El servidor valida participaciones y
 * sella el evento en la bitácora; aquí sólo se envía el estado deseado completo.
 */
export async function guardarGrafoBc(
  token: string,
  personaCanonical: string,
  nodos: BcNodo[],
  aristas: BcArista[],
): Promise<BcGuardarResultado> {
  return json<BcGuardarResultado>(
    await req("/rest/v1/rpc/bc_guardar_grafo", {
      token,
      body: { p_persona_canonical: personaCanonical, p_nodos: nodos, p_aristas: aristas },
    }),
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Expediente (0021): cuestionario condicionado + documentos
// ─────────────────────────────────────────────────────────────────────────────

export interface RequisitoEstado {
  clave: string;
  nombre: string;
  descripcion: string | null;
  requiere_documento: boolean;
  obligatorio: boolean;
  es_ddr: boolean;
  satisfecho: boolean;
  documento_id: string | null;
  // Requisito de beneficiario controlador (0060): además del documento exige el
  // grafo de control capturado. requiere_grafo_bc lo marca; grafo_bc_resuelto es
  // el estado del grafo (true/false para esos requisitos, null para el resto).
  requiere_grafo_bc: boolean;
  grafo_bc_resuelto: boolean | null;
}

export async function expedienteEstado(token: string, clienteId: string): Promise<RequisitoEstado[]> {
  return json<RequisitoEstado[]>(
    await req("/rest/v1/rpc/expediente_estado", { token, body: { p_cliente: clienteId } }),
  );
}

// Constantes de codificación (no montos).
const HEX_BASE = 16;
const HEX_ANCHO = 2;
/** Nº de caracteres del hash a mostrar en mensajes de error (prefijo legible). */
const HASH_PREVIEW = 12;

/** SHA-256 del archivo, en hex — la huella que exige la bóveda (documento.hash_sha256). */
export async function sha256Hex(datos: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", datos);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(HEX_BASE).padStart(HEX_ANCHO, "0"))
    .join("");
}

export const BUCKET_EXPEDIENTES = "expedientes";
const LONGITUD_FECHA_ISO = "YYYY-MM-DD".length;

/**
 * Error de integridad probatoria de la bóveda (issue #44). Distingue las dos
 * fallas de la cadena subida→registro para que la UI y el operador las vean:
 *  - "hash_mismatch": lo que aterrizó en Storage NO coincide con el archivo
 *    original → corrupción en tránsito. El objeto se deja marcado como
 *    sospechoso (ver `objetoSospechoso`) y NO se registra fila en `documento`.
 *  - "huerfano": el objeto se subió y verificó, pero el INSERT en `documento`
 *    falló → objeto en Storage sin registro (huérfano). El script de
 *    conciliación (scripts/reconcile-boveda.ts) lo detecta de forma durable.
 *
 * Compensación: el frontend NO puede borrar el objeto (storage.objects no tiene
 * política DELETE para usuarios de tenant — migración 0022: la bóveda es
 * inmutable). Por eso la compensación es "marcar huérfano + alerta visible",
 * no "borrar el objeto". La limpieza definitiva del huérfano es proceso de
 * sistema (service_role), igual que la liberación por retención.
 */
export class BovedaIntegridadError extends ApiError {
  constructor(
    mensaje: string,
    readonly tipo: "hash_mismatch" | "huerfano",
    readonly storagePath: string,
    status?: number,
  ) {
    super(mensaje, status);
    this.name = "BovedaIntegridadError";
  }
}

/**
 * Descarga el objeto recién subido y re-calcula su SHA-256, comparándolo contra
 * el hash del archivo original (issue #44, criterio 1). Invariante vigente:
 * error fatal si el hash post-upload difiere.
 *
 * Se hace en el frontend (no en una Edge Function) por consistencia con ADR
 * 0001: el cliente ya calcula el hash de origen, habla REST directo, y tiene
 * SELECT sobre el bucket `expedientes` bajo su prefijo de tenant (migración
 * 0022), de modo que puede leer de vuelta el objeto dentro de su propio RLS sin
 * introducir una nueva superficie de deploy.
 *
 * Lanza BovedaIntegridadError("hash_mismatch") si difiere: el objeto quedó en
 * Storage pero NO se debe registrar (integridad rota) y NO se puede borrar
 * desde el cliente → queda como sospechoso para el script de conciliación.
 */
async function verificarHashPostUpload(args: {
  token: string;
  ruta: string;
  hashEsperado: string;
}): Promise<void> {
  const res = await req(`/storage/v1/object/${BUCKET_EXPEDIENTES}/${args.ruta}`, {
    token: args.token,
  });
  const hashSubido = await sha256Hex(await res.arrayBuffer());
  if (hashSubido !== args.hashEsperado) {
    throw new BovedaIntegridadError(
      "El archivo que aterrizó en la bóveda no coincide con el original " +
        `(SHA-256 esperado ${args.hashEsperado.slice(0, HASH_PREVIEW)}…, obtenido ` +
        `${hashSubido.slice(0, HASH_PREVIEW)}…). No se registró el documento; el objeto ` +
        "quedó marcado como sospechoso para conciliación. No vuelvas a confiar " +
        "en ese archivo: súbelo de nuevo.",
      "hash_mismatch",
      `${BUCKET_EXPEDIENTES}/${args.ruta}`,
    );
  }
}

/**
 * Registra la fila en `documento` para un objeto YA subido y verificado. Si el
 * INSERT falla, el objeto queda huérfano en Storage (no borrable desde el
 * cliente); se convierte la falla en BovedaIntegridadError("huerfano") con la
 * ruta, para alerta visible inmediata + captura durable por el reconciliador.
 */
async function registrarDocumentoVerificado(args: {
  token: string;
  ruta: string;
  body: Record<string, unknown>;
  headers?: Record<string, string>;
  /** Sufijo de query PostgREST (p.ej. "?select=id"). */
  query?: string;
}): Promise<Response> {
  try {
    return await req(`/rest/v1/documento${args.query ?? ""}`, {
      token: args.token,
      body: args.body,
      ...(args.headers ? { headers: args.headers } : {}),
    });
  } catch (e) {
    const causa = e instanceof Error ? e.message : String(e);
    const status = e instanceof ApiError ? e.status : undefined;
    throw new BovedaIntegridadError(
      "El archivo se subió y verificó, pero no se pudo registrar en la bóveda " +
        `(${causa}). Quedó un objeto huérfano en Storage que la conciliación ` +
        "reportará. Reintenta la subida.",
      "huerfano",
      `${BUCKET_EXPEDIENTES}/${args.ruta}`,
      status,
    );
  }
}

function nombreArchivoSeguro(nombre: string): string {
  return nombre.replace(/[^A-Za-z0-9._-]/g, "_");
}

function hoyIso(): string {
  return new Date().toISOString().slice(0, LONGITUD_FECHA_ISO);
}

async function subirYRegistrarDocumento(args: {
  token: string;
  tenantId: string;
  archivo: File;
  rutaSegmentos: string[];
  columnas: (hoy: string) => Record<string, unknown>;
  query?: string;
  headers?: Record<string, string>;
}): Promise<Response> {
  const hash = await sha256Hex(await args.archivo.arrayBuffer());
  const ruta = `${args.tenantId}/${args.rutaSegmentos.join("/")}/${String(Date.now())}-${nombreArchivoSeguro(args.archivo.name)}`;

  await req(`/storage/v1/object/${BUCKET_EXPEDIENTES}/${ruta}`, {
    token: args.token,
    raw: args.archivo,
    headers: {
      "Content-Type": args.archivo.type || "application/octet-stream",
      "x-upsert": "false",
    },
  });

  await verificarHashPostUpload({ token: args.token, ruta, hashEsperado: hash });

  const hoy = hoyIso();
  return registrarDocumentoVerificado({
    token: args.token,
    ruta,
    body: {
      tenant_id: args.tenantId,
      fecha_acto: hoy,
      hash_sha256: hash,
      storage_path: `${BUCKET_EXPEDIENTES}/${ruta}`,
      nombre_archivo: args.archivo.name,
      ...args.columnas(hoy),
    },
    ...(args.query ? { query: args.query } : {}),
    ...(args.headers ? { headers: args.headers } : {}),
  });
}

async function subirAcuseGenerico(args: {
  token: string;
  tenantId: string;
  entidad: "formato_exportacion" | "informe";
  entidadId: string;
  archivo: File;
  mensajeVacio: string;
}): Promise<string> {
  const filas = await json<{ id: string }[]>(
    await subirYRegistrarDocumento({
      token: args.token,
      tenantId: args.tenantId,
      archivo: args.archivo,
      rutaSegmentos: [args.entidad, args.entidadId, "acuse"],
      query: "?select=id",
      columnas: (hoy) => ({ fecha_acuse: hoy }),
      headers: { Prefer: "return=representation" },
    }),
  );
  const fila = filas[0];
  if (!fila) throw new ApiError(args.mensajeVacio);
  return fila.id;
}

/**
 * Sube un archivo del expediente y registra su fila en `documento` (hash +
 * enlace a cliente y requisito). La retención (10 años) la deriva el trigger
 * de la bóveda; aquí sólo viaja la fecha del acto (hoy).
 *
 * Cadena de integridad (issue #44): 1) hash del origen → 2) subida →
 * 3) verificación post-upload (descarga + re-hash) → 4) INSERT con
 * compensación por huérfano si el registro falla.
 */
export async function subirDocumentoExpediente(args: {
  token: string;
  tenantId: string;
  clienteId: string;
  requisitoClave: string;
  archivo: File;
}): Promise<void> {
  await subirYRegistrarDocumento({
    token: args.token,
    tenantId: args.tenantId,
    archivo: args.archivo,
    rutaSegmentos: [args.clienteId, args.requisitoClave],
    columnas: () => ({
      cliente_aportante_id: args.clienteId,
      requisito_clave: args.requisitoClave,
    }),
  });
}

export async function subirAcuseFormatoExportacion(args: {
  token: string;
  tenantId: string;
  exportacionId: string;
  archivo: File;
}): Promise<string> {
  return subirAcuseGenerico({
    token: args.token,
    tenantId: args.tenantId,
    entidad: "formato_exportacion",
    entidadId: args.exportacionId,
    archivo: args.archivo,
    mensajeVacio: "El acuse no devolvió documento de bóveda.",
  });
}

export async function subirAcuseInforme(args: {
  token: string;
  tenantId: string;
  informeId: string;
  archivo: File;
}): Promise<string> {
  return subirAcuseGenerico({
    token: args.token,
    tenantId: args.tenantId,
    entidad: "informe",
    entidadId: args.informeId,
    archivo: args.archivo,
    mensajeVacio: "El acuse no devolvió documento de bóveda.",
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Proyectos
// ─────────────────────────────────────────────────────────────────────────────

export interface ProyectoMin {
  id: string;
  nombre: string;
}

export async function listarProyectos(token: string): Promise<ProyectoMin[]> {
  return json<ProyectoMin[]>(
    await req("/rest/v1/proyecto?select=id,nombre&order=created_at.desc", { token }),
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Captación (aportacion) — el motor evalúa al insertar (trigger) y registra la
// evaluación en bitácora (accion = 'aportacion.evaluada'); de ahí lee la UI.
// ─────────────────────────────────────────────────────────────────────────────

export type PersonaEmbed = Pick<
  Tables<"cliente_aportante">,
  | "id"
  | "tipo"
  | "nombre"
  | "apellido_paterno"
  | "apellido_materno"
  | "denominacion_razon"
  | "fiduciario_denominacion"
  | "fideicomiso_identificador"
  | "expediente_completo"
>;

/** Columnas `numeric(18,2)` de Postgres: PostgREST las serializa como STRING en
 * JSON (evita pérdida de precisión de floats), pero el generador de Supabase
 * las tipa `number` porque refleja el tipo de columna, no el comportamiento
 * de serialización del wire. Drift conocido generador↔runtime (issue #42):
 * se sobreescribe aquí a `number | string` para no romper el runtime real,
 * que ya maneja ambos casos vía el helper `dinero()` (ui/theme.ts).
 */
type ColumnaNumericComoTexto = number | string;

/**
 * Fila de `aportacion` derivada del esquema real + embeds de PostgREST
 * (cliente_aportante, proyecto). Drift corregido: `tipo_aportacion` era
 * `string | null` a mano; la columna real es el enum `tipo_aportacion_din | null`.
 * `moneda` era `string | null` en la interfaz manual pero la columna es
 * NOT NULL con default ('MXN') — se mantiene `string` (no nullable) reflejando
 * el esquema real.
 */
export type AportacionRow = Pick<
  Tables<"aportacion">,
  "id" | "fecha_acto" | "es_efectivo" | "instrumento_monetario" | "forma_pago_inm" | "tipo_aportacion" | "aportacion_clase" |
  "es_preventa" | "moneda" | "plazo_meses" | "tipo_credito" | "tipo_institucion" | "fecha_emision" | "created_at"
> & {
  monto_base: ColumnaNumericComoTexto;
  monto_contribuciones: ColumnaNumericComoTexto;
  monto_bruto: ColumnaNumericComoTexto;
  descripcion_bien: string | null;
  monto_estimado: ColumnaNumericComoTexto | null;
  valor_inmueble_preventa: ColumnaNumericComoTexto | null;
  monto_solicitado: ColumnaNumericComoTexto | null;
  cliente_aportante: PersonaEmbed | null;
  proyecto: ProyectoMin | null;
};

const APORTACION_COLS =
  "id,fecha_acto,monto_base,monto_contribuciones,monto_bruto,es_efectivo,instrumento_monetario," +
  "forma_pago_inm,tipo_aportacion,aportacion_clase,descripcion_bien,monto_estimado,es_preventa,valor_inmueble_preventa,moneda,plazo_meses,tipo_credito," +
  "tipo_institucion,monto_solicitado,fecha_emision,created_at," +
  "cliente_aportante(id,tipo,nombre,apellido_paterno,apellido_materno,denominacion_razon," +
  "fiduciario_denominacion,fideicomiso_identificador,expediente_completo),proyecto(id,nombre)";

export async function listarAportaciones(token: string): Promise<AportacionRow[]> {
  return json<AportacionRow[]>(
    await req(`/rest/v1/aportacion?select=${APORTACION_COLS}&order=fecha_acto.desc,created_at.desc`, { token }),
  );
}

export async function crearAportacion(
  token: string,
  tenantId: string,
  datos: Record<string, unknown>,
): Promise<AportacionRow> {
  return crearEntidad<AportacionRow>({
    token,
    tenantId,
    tabla: "aportacion",
    select: APORTACION_COLS,
    datos,
    mensajeVacio: "El alta no devolvió la aportación creada.",
  });
}

/** Resultado estructurado de evaluar_aportacion (motor de captura, 0006/0016). */
export interface EvaluacionMotor {
  semaforo?: string;
  fraccion_efectiva?: string | null;
  base_individual?: number;
  acumulado_persona?: number;
  umbral_pesos?: number;
  excede_individual?: boolean;
  excede_acumulado?: boolean;
  requiere_aviso?: boolean;
  estado_exigibilidad?: string;
  prohibicion_efectivo?: boolean;
  prohibicion_indeterminada?: boolean;
  art32_encuadre?: string;
  vence_aviso?: string | null;
  formato_aviso?: string | null;
  formato_oficial?: string | null;
  adaptador_pendiente?: boolean;
  aviso_sin_formato?: boolean;
}

interface BitacoraEval {
  entidad_id: string;
  contenido: EvaluacionMotor;
  seq: number;
}

/** Última evaluación del motor por aportación (leída de la bitácora). */
export async function evaluacionesUltimas(
  token: string,
  aportacionIds: string[],
): Promise<Map<string, EvaluacionMotor>> {
  const mapa = new Map<string, EvaluacionMotor>();
  if (aportacionIds.length === 0) return mapa;
  const lista = aportacionIds.map((i) => `"${i}"`).join(",");
  const filas = await json<BitacoraEval[]>(
    await req(
      `/rest/v1/bitacora?select=entidad_id,contenido,seq&accion=eq.aportacion.evaluada&entidad_id=in.(${lista})&order=seq.desc`,
      { token },
    ),
  );
  for (const f of filas) {
    if (!mapa.has(f.entidad_id)) mapa.set(f.entidad_id, f.contenido);
  }
  return mapa;
}

export interface AvisoDestino {
  aviso: {
    id: string;
    referencia_aviso: string | null;
    informe: { id: string; mes_reportado: string; estado: string } | null;
  } | null;
}

export interface AvisoInformeRow {
  id: string;
  informe_id: string;
  modalidad: string;
  prioridad: number;
  tipo_alerta: string;
  descripcion_alerta: string | null;
  referencia_aviso: string | null;
}

export async function listarAvisosInforme(token: string, informeId: string): Promise<AvisoInformeRow[]> {
  return json<AvisoInformeRow[]>(
    await req(
      `/rest/v1/aviso?select=id,informe_id,modalidad,prioridad,tipo_alerta,descripcion_alerta,referencia_aviso&informe_id=eq.${informeId}&order=created_at.asc`,
      { token },
    ),
  );
}

export async function configurarAlertaAviso(
  token: string,
  avisoId: string,
  prioridad: number,
  tipoAlerta: string,
  descripcionAlerta: string | null,
): Promise<void> {
  await req("/rest/v1/rpc/configurar_alerta_aviso", {
    token,
    body: {
      p_aviso_id: avisoId,
      p_prioridad: prioridad,
      p_tipo_alerta: tipoAlerta,
      p_descripcion_alerta: descripcionAlerta,
    },
  });
}

/** ¿En qué aviso/informe quedó ligada la aportación? (liga 0013). */
export async function destinoDeAportacion(token: string, aportacionId: string): Promise<AvisoDestino[]> {
  return json<AvisoDestino[]>(
    await req(
      `/rest/v1/aviso_aportacion?select=aviso(id,referencia_aviso,informe(id,mes_reportado,estado))&aportacion_id=eq.${aportacionId}`,
      { token },
    ),
  );
}

/**
 * Fila de `formato_exportacion` derivada del esquema real. Drift corregido:
 * `estado` era `string` a mano; la columna real es el enum
 * `estado_formato_exportacion` (borrador|generado|validado|presentado|
 * acuse_capturado|cancelado|error).
 */
export type FormatoExportacionRow = Pick<
  Tables<"formato_exportacion">,
  | "id"
  | "formato_codigo"
  | "aportacion_id"
  | "informe_id"
  | "estado"
  | "prioridad"
  | "tipo_alerta"
  | "descripcion_alerta"
  | "xml_sha256"
  | "folio_acuse"
  | "acuse_documento_id"
  | "presentado_en"
  | "acuse_en"
  | "created_at"
>;

export async function listarExportacionesFormato(token: string): Promise<FormatoExportacionRow[]> {
  return json<FormatoExportacionRow[]>(
    await req(
      "/rest/v1/formato_exportacion?select=id,formato_codigo,aportacion_id,informe_id,estado,prioridad,tipo_alerta,descripcion_alerta,xml_sha256,folio_acuse,acuse_documento_id,presentado_en,acuse_en,created_at&order=created_at.desc",
      { token },
    ),
  );
}

export async function configurarAlertaFormatoExportacion(
  token: string,
  exportacionId: string,
  prioridad: number,
  tipoAlerta: string,
  descripcionAlerta: string | null,
): Promise<void> {
  await req("/rest/v1/rpc/configurar_alerta_formato_exportacion", {
    token,
    body: {
      p_exportacion_id: exportacionId,
      p_prioridad: prioridad,
      p_tipo_alerta: tipoAlerta,
      p_descripcion_alerta: descripcionAlerta,
    },
  });
}

export async function generarXmlInmuebles(token: string, exportacionId: string): Promise<void> {
  await req("/functions/v1/generar-xml-inmuebles", {
    token,
    body: { exportacion_id: exportacionId },
  });
}

export async function generarXmlArrendamiento(token: string, exportacionId: string): Promise<void> {
  await req("/functions/v1/generar-xml-arrendamiento", {
    token,
    body: { exportacion_id: exportacionId },
  });
}

/**
 * Genera el XML de la exportación (borrador por aportación) enrutando por
 * formato: INMUEBLES_XML → generar-xml-inmuebles; ARRENDAMIENTO_XML →
 * generar-xml-arrendamiento (OT-7). Espejo de generarXmlInforme.
 *
 * Compuerta BC (issue BC-3): si el cliente es PM/fideicomiso y no hay
 * beneficiario controlador, la función lanza ApiError con status 409 y
 * `code === CODIGO_BC_AUSENTE`. El REC confirma reintentando con
 * `confirmarSinBc = true`, que sella la ausencia en bitácora y genera el aviso.
 */
export async function generarXmlExportacion(
  token: string,
  exportacionId: string,
  formatoCodigo: string,
  confirmarSinBc = false,
): Promise<void> {
  const fn = formatoCodigo === "ARRENDAMIENTO_XML" ? "generar-xml-arrendamiento" : "generar-xml-inmuebles";
  const body: Record<string, unknown> = { exportacion_id: exportacionId };
  if (confirmarSinBc) body.confirmar_sin_bc = true;
  await req(`/functions/v1/${fn}`, { token, body });
}

export async function obtenerXmlExportacion(token: string, exportacionId: string): Promise<string | null> {
  const filas = await json<{ xml_generado: string | null }[]>(
    await req(`/rest/v1/formato_exportacion?select=xml_generado&id=eq.${exportacionId}`, { token }),
  );
  return filas[0]?.xml_generado ?? null;
}

export async function validarFormatoExportacion(token: string, exportacionId: string): Promise<void> {
  await req("/rest/v1/rpc/validar_formato_exportacion", { token, body: { p_exportacion_id: exportacionId } });
}

export async function presentarFormatoExportacion(
  token: string,
  exportacionId: string,
): Promise<ResultadoPresentacion> {
  return json<ResultadoPresentacion>(
    await req("/rest/v1/rpc/presentar_formato_exportacion", { token, body: { p_exportacion_id: exportacionId } }),
  );
}

export async function capturarAcuseFormatoExportacion(
  token: string,
  exportacionId: string,
  folio: string,
  documentoId: string,
): Promise<void> {
  await req("/rest/v1/rpc/capturar_acuse_formato_exportacion", {
    token,
    body: { p_exportacion_id: exportacionId, p_folio: folio, p_documento_id: documentoId },
  });
}

export async function cancelarFormatoExportacion(
  token: string,
  exportacionId: string,
  motivo: string,
): Promise<void> {
  await req("/rest/v1/rpc/cancelar_formato_exportacion", {
    token,
    body: { p_exportacion_id: exportacionId, p_motivo: motivo || null },
  });
}

export interface HipotesisNormativa {
  aportacion_id: string;
  evento_id: string;
  fraccion: string;
  estado: string;
  fundamento: string | null;
  base_calculo: string | null;
  acumulacion: string | null;
  base_individual: number | string | null;
  acumulado_directo: number | string | null;
  acumulado_bc: number | string | null;
  umbral_pesos: number | string | null;
  excede_individual: boolean;
  excede_acumulado_directo: boolean;
  excede_acumulado_bc: boolean;
  requiere_aviso: boolean;
  formato_aviso: string | null;
  formato_oficial: string | null;
  adaptador_pendiente: boolean;
  metadatos: Record<string, unknown>;
}

export async function hipotesisPorAportaciones(
  token: string,
  aportacionIds: string[],
): Promise<Map<string, HipotesisNormativa[]>> {
  const mapa = new Map<string, HipotesisNormativa[]>();
  if (aportacionIds.length === 0) return mapa;
  const filas = await json<HipotesisNormativa[]>(
    await req("/rest/v1/rpc/hipotesis_por_aportacion", {
      token,
      body: { p_aportacion_ids: aportacionIds },
    }),
  );
  for (const fila of filas) {
    const xs = mapa.get(fila.aportacion_id) ?? [];
    xs.push(fila);
    mapa.set(fila.aportacion_id, xs);
  }
  return mapa;
}

// ─────────────────────────────────────────────────────────────────────────────
// Reportes (informe DIN) — máquina de estados 0014 + generador E6
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fila de `informe` derivada del esquema real. Drift corregido: `estado` era
 * `string` a mano; la columna real es el enum `estado_informe`
 * (borrador|generado|firmado|presentado|acuse_capturado|cancelado).
 */
export type InformeRow = Pick<
  Tables<"informe">,
  | "id"
  | "mes_reportado"
  | "formato_codigo"
  | "estado"
  | "es_en_ceros"
  | "vence_en"
  | "xml_sha256"
  | "folio_acuse"
  | "acuse_documento_id"
  | "presentado_en"
  | "acuse_en"
  | "modificatorio_de"
>;

/** Enum real de `informe.estado` (borrador|generado|firmado|presentado|acuse_capturado|cancelado). */
export type EstadoInforme = InformeRow["estado"];

export async function listarInformes(token: string): Promise<InformeRow[]> {
  return json<InformeRow[]>(
    await req(
      "/rest/v1/informe?select=id,mes_reportado,formato_codigo,estado,es_en_ceros,vence_en,xml_sha256,folio_acuse,acuse_documento_id,presentado_en,acuse_en,modificatorio_de&order=mes_reportado.desc",
      { token },
    ),
  );
}

/**
 * Aviso modificatorio (Art. 8): sólo procede sobre un informe ORIGINAL
 * acuse_capturado, dentro de 30 días de acuse_en, y que aún no tenga uno. El RPC
 * valida las mismas cotas server-side; esto es sólo para habilitar/ocultar el
 * botón en la UI.
 */
export const VENTANA_MODIFICATORIO_DIAS = 30;
const MS_POR_SEGUNDO = 1000;
const SEGUNDOS_POR_MINUTO = 60;
const MINUTOS_POR_HORA = 60;
const HORAS_POR_DIA = 24;
const MS_POR_DIA = MS_POR_SEGUNDO * SEGUNDOS_POR_MINUTO * MINUTOS_POR_HORA * HORAS_POR_DIA;

export function procedeModificatorio(inf: InformeRow, todos: InformeRow[]): boolean {
  if (inf.estado !== "acuse_capturado" || !inf.acuse_en) return false;
  const dias = (Date.now() - new Date(inf.acuse_en).getTime()) / MS_POR_DIA;
  if (dias > VENTANA_MODIFICATORIO_DIAS) return false;
  return !todos.some((x) => x.modificatorio_de === inf.id);
}

export interface ResultadoModificatorio {
  ok?: boolean;
  informe_id?: string;
  informe_original_id?: string;
  folio_original?: string | null;
  estado?: string;
}

export async function crearInformeModificatorio(
  token: string,
  informeId: string,
  descripcion: string,
): Promise<ResultadoModificatorio> {
  return json<ResultadoModificatorio>(
    await req("/rest/v1/rpc/crear_informe_modificatorio", {
      token,
      body: { p_informe_id: informeId, p_descripcion: descripcion },
    }),
  );
}

export async function avisosPorInforme(token: string): Promise<Map<string, number>> {
  const filas = await json<{ informe_id: string }[]>(
    await req("/rest/v1/aviso?select=informe_id", { token }),
  );
  const mapa = new Map<string, number>();
  for (const f of filas) {
    mapa.set(f.informe_id, (mapa.get(f.informe_id) ?? 0) + 1);
  }
  return mapa;
}

export async function obtenerXmlInforme(token: string, informeId: string): Promise<string | null> {
  const filas = await json<{ xml_generado: string | null }[]>(
    await req(`/rest/v1/informe?select=xml_generado&id=eq.${informeId}`, { token }),
  );
  return filas[0]?.xml_generado ?? null;
}

/**
 * Genera el XML del informe mensual y avanza borrador→generado. Enruta por formato:
 * ANEXO_5B → generar-informe-din (DIN); INMUEBLES_XML → generar-xml-inmuebles;
 * ARRENDAMIENTO_XML → generar-xml-arrendamiento (OT-7) en modo informe (arma N
 * avisos de las exportaciones ligadas al informe del mes).
 */
export async function generarXmlInforme(
  token: string,
  informeId: string,
  formatoCodigo = "ANEXO_5B",
): Promise<void> {
  const fn = formatoCodigo === "INMUEBLES_XML"
    ? "generar-xml-inmuebles"
    : formatoCodigo === "ARRENDAMIENTO_XML"
      ? "generar-xml-arrendamiento"
      : "generar-informe-din";
  await req(`/functions/v1/${fn}`, { token, body: { informe_id: informeId } });
}

export async function validarInforme(token: string, informeId: string): Promise<void> {
  await req("/rest/v1/rpc/validar_informe", { token, body: { p_informe_id: informeId } });
}

export interface ResultadoPresentacion {
  estado?: string;
  efirma_vigente?: boolean;
  xml_sha256?: string;
  advertencia_efirma?: string | null;
}

export async function presentarInforme(token: string, informeId: string): Promise<ResultadoPresentacion> {
  return json<ResultadoPresentacion>(
    await req("/rest/v1/rpc/presentar_informe", { token, body: { p_informe_id: informeId } }),
  );
}

export async function capturarAcuseInforme(
  token: string,
  informeId: string,
  folio: string,
  documentoId: string,
): Promise<void> {
  await req("/rest/v1/rpc/capturar_acuse_informe", {
    token,
    body: { p_informe_id: informeId, p_folio: folio, p_documento_id: documentoId },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Compraventas (fr. V, 0023) — los cobros son aportaciones con compraventa_id
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fila de `compraventa` derivada del esquema real. Drift corregido: `etapa`
 * era `string` a mano; la columna real es el enum `etapa_compraventa`
 * ("apartado" | "contrato_privado" | "escriturada"). Los campos `numeric`
 * (precio_pactado, dimension_*, valor_avaluo_catastral) se sobreescriben a
 * `number | string` por el mismo motivo que en AportacionRow (PostgREST
 * serializa numeric como string en el wire).
 */
export type CompraventaRow = Pick<
  Tables<"compraventa">,
  "id" | "unidad" | "etapa" | "fecha_operacion" | "tipo_operacion_inm" | "figura_cliente" | "figura_so" |
  "tipo_inmueble" | "inmueble_colonia" | "inmueble_calle" | "inmueble_numero_exterior" | "inmueble_numero_interior" |
  "inmueble_codigo_postal" | "folio_real" | "instrumento_tipo" | "fecha_contrato" | "numero_instrumento_publico" |
  "fecha_instrumento_publico" | "notario_instrumento_publico" | "entidad_instrumento_publico" | "created_at"
> & {
  precio_pactado: ColumnaNumericComoTexto;
  dimension_terreno: ColumnaNumericComoTexto | null;
  dimension_construido: ColumnaNumericComoTexto | null;
  valor_avaluo_catastral: ColumnaNumericComoTexto | null;
  cliente_aportante: PersonaEmbed | null;
  proyecto: ProyectoMin | null;
};

/** Enum real de `compraventa.etapa` (apartado|contrato_privado|escriturada). */
export type EtapaCompraventa = CompraventaRow["etapa"];

const COMPRAVENTA_COLS =
  "id,unidad,etapa,precio_pactado,fecha_operacion,tipo_operacion_inm,figura_cliente,figura_so," +
  "tipo_inmueble,inmueble_colonia,inmueble_calle,inmueble_numero_exterior,inmueble_numero_interior," +
  "inmueble_codigo_postal,dimension_terreno,dimension_construido,folio_real,instrumento_tipo,fecha_contrato," +
  "numero_instrumento_publico,fecha_instrumento_publico,notario_instrumento_publico,entidad_instrumento_publico," +
  "valor_avaluo_catastral,created_at," +
  "cliente_aportante(id,tipo,nombre,apellido_paterno,apellido_materno,denominacion_razon," +
  "fiduciario_denominacion,fideicomiso_identificador,expediente_completo),proyecto(id,nombre)";

export async function listarCompraventas(token: string): Promise<CompraventaRow[]> {
  return json<CompraventaRow[]>(
    await req(`/rest/v1/compraventa?select=${COMPRAVENTA_COLS}&order=created_at.desc`, { token }),
  );
}

export async function crearCompraventa(
  token: string,
  tenantId: string,
  datos: Record<string, unknown>,
): Promise<CompraventaRow> {
  return crearEntidad<CompraventaRow>({
    token,
    tenantId,
    tabla: "compraventa",
    select: COMPRAVENTA_COLS,
    datos,
    mensajeVacio: "El alta no devolvió la compraventa creada.",
  });
}

export async function actualizarCompraventa(
  token: string,
  id: string,
  patch: Record<string, unknown>,
): Promise<void> {
  await req(`/rest/v1/compraventa?id=eq.${id}`, { method: "PATCH", token, body: patch });
}

/** Cobros de una compraventa (aportaciones ligadas), más recientes primero. */
export async function cobrosDeCompraventa(token: string, compraventaId: string): Promise<AportacionRow[]> {
  return json<AportacionRow[]>(
    await req(
      `/rest/v1/aportacion?select=${APORTACION_COLS}&compraventa_id=eq.${compraventaId}&order=fecha_acto.desc`,
      { token },
    ),
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// INM cardinalidad 1..N (migración 0059): copropietarios (#66), inmuebles
// adicionales (#67) y contrapartes de intermediario (#68). Tipos ESCRITOS A MANO
// (no derivados de Tables<>) porque types.ts aún no incluye estas tablas — lo
// regenera el coordinador al consolidar. El nombre del copropietario/contraparte
// lo resuelve la vista desde la lista de personas ya cargada (sin embed).
// ─────────────────────────────────────────────────────────────────────────────

export interface CopropietarioRow {
  id: string;
  cliente_aportante_id: string;
  tipo_participacion: string;
  porcentaje_indiviso: ColumnaNumericComoTexto | null;
  orden: number;
  created_at: string;
}

const COPROPIETARIO_COLS = "id,cliente_aportante_id,tipo_participacion,porcentaje_indiviso,orden,created_at";

export async function listarCopropietarios(token: string, compraventaId: string): Promise<CopropietarioRow[]> {
  return json<CopropietarioRow[]>(
    await req(
      `/rest/v1/compraventa_persona?select=${COPROPIETARIO_COLS}&compraventa_id=eq.${compraventaId}&order=orden.asc,created_at.asc`,
      { token },
    ),
  );
}

export async function agregarCopropietario(
  token: string,
  tenantId: string,
  datos: Record<string, unknown>,
): Promise<CopropietarioRow> {
  return crearEntidad<CopropietarioRow>({
    token,
    tenantId,
    tabla: "compraventa_persona",
    select: COPROPIETARIO_COLS,
    datos,
    mensajeVacio: "El alta no devolvió el copropietario creado.",
  });
}

export async function eliminarCopropietario(token: string, id: string): Promise<void> {
  await req(`/rest/v1/compraventa_persona?id=eq.${id}`, { method: "DELETE", token });
}

export interface CompraventaInmuebleRow {
  id: string;
  orden: number;
  descripcion: string | null;
  tipo_inmueble: string;
  inmueble_colonia: string;
  inmueble_calle: string;
  inmueble_numero_exterior: string;
  inmueble_numero_interior: string | null;
  inmueble_codigo_postal: string;
  dimension_terreno: ColumnaNumericComoTexto;
  dimension_construido: ColumnaNumericComoTexto;
  folio_real: string;
  valor_pactado: ColumnaNumericComoTexto;
  created_at: string;
}

const COMPRAVENTA_INMUEBLE_COLS =
  "id,orden,descripcion,tipo_inmueble,inmueble_colonia,inmueble_calle,inmueble_numero_exterior," +
  "inmueble_numero_interior,inmueble_codigo_postal,dimension_terreno,dimension_construido,folio_real," +
  "valor_pactado,created_at";

export async function listarInmueblesAdicionales(token: string, compraventaId: string): Promise<CompraventaInmuebleRow[]> {
  return json<CompraventaInmuebleRow[]>(
    await req(
      `/rest/v1/compraventa_inmueble?select=${COMPRAVENTA_INMUEBLE_COLS}&compraventa_id=eq.${compraventaId}&order=orden.asc,created_at.asc`,
      { token },
    ),
  );
}

export async function agregarInmuebleAdicional(
  token: string,
  tenantId: string,
  datos: Record<string, unknown>,
): Promise<CompraventaInmuebleRow> {
  return crearEntidad<CompraventaInmuebleRow>({
    token,
    tenantId,
    tabla: "compraventa_inmueble",
    select: COMPRAVENTA_INMUEBLE_COLS,
    datos,
    mensajeVacio: "El alta no devolvió el inmueble adicional creado.",
  });
}

export async function eliminarInmuebleAdicional(token: string, id: string): Promise<void> {
  await req(`/rest/v1/compraventa_inmueble?id=eq.${id}`, { method: "DELETE", token });
}

export interface ContraparteRow {
  id: string;
  cliente_aportante_id: string;
  rol: string;
  orden: number;
  created_at: string;
}

const CONTRAPARTE_COLS = "id,cliente_aportante_id,rol,orden,created_at";

export async function listarContrapartes(token: string, compraventaId: string): Promise<ContraparteRow[]> {
  return json<ContraparteRow[]>(
    await req(
      `/rest/v1/compraventa_contraparte?select=${CONTRAPARTE_COLS}&compraventa_id=eq.${compraventaId}&order=orden.asc,created_at.asc`,
      { token },
    ),
  );
}

export async function agregarContraparte(
  token: string,
  tenantId: string,
  datos: Record<string, unknown>,
): Promise<ContraparteRow> {
  return crearEntidad<ContraparteRow>({
    token,
    tenantId,
    tabla: "compraventa_contraparte",
    select: CONTRAPARTE_COLS,
    datos,
    mensajeVacio: "El alta no devolvió la contraparte creada.",
  });
}

export async function eliminarContraparte(token: string, id: string): Promise<void> {
  await req(`/rest/v1/compraventa_contraparte?id=eq.${id}`, { method: "DELETE", token });
}

// ─────────────────────────────────────────────────────────────────────────────
// Arrendamientos (fr. XV, OT-7) — los cobros de renta son aportaciones con
// arrendamiento_id. Espejo de Compraventas (fr. V).
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fila de `arrendamiento` derivada del esquema real. Sin drift de tipos:
 * la interfaz manual anterior ya coincidía columna a columna (única
 * diferencia es `renta_mensual`, numeric → serializado como string por
 * PostgREST, igual que en Aportacion/Compraventa).
 */
export type ArrendamientoRow = Pick<
  Tables<"arrendamiento">,
  "id" | "unidad" | "fecha_operacion" | "fecha_inicio" | "fecha_termino" | "figura_cliente" | "tipo_inmueble" |
  "inmueble_colonia" | "inmueble_calle" | "inmueble_numero_exterior" | "inmueble_numero_interior" |
  "inmueble_codigo_postal" | "folio_real" | "created_at"
> & {
  renta_mensual: ColumnaNumericComoTexto;
  cliente_aportante: PersonaEmbed | null;
  proyecto: ProyectoMin | null;
};

const ARRENDAMIENTO_COLS =
  "id,unidad,renta_mensual,fecha_operacion,fecha_inicio,fecha_termino,figura_cliente," +
  "tipo_inmueble,inmueble_colonia,inmueble_calle,inmueble_numero_exterior,inmueble_numero_interior," +
  "inmueble_codigo_postal,folio_real,created_at," +
  "cliente_aportante(id,tipo,nombre,apellido_paterno,apellido_materno,denominacion_razon," +
  "fiduciario_denominacion,fideicomiso_identificador,expediente_completo),proyecto(id,nombre)";

export async function listarArrendamientos(token: string): Promise<ArrendamientoRow[]> {
  return json<ArrendamientoRow[]>(
    await req(`/rest/v1/arrendamiento?select=${ARRENDAMIENTO_COLS}&order=created_at.desc`, { token }),
  );
}

export async function crearArrendamiento(
  token: string,
  tenantId: string,
  datos: Record<string, unknown>,
): Promise<ArrendamientoRow> {
  return crearEntidad<ArrendamientoRow>({
    token,
    tenantId,
    tabla: "arrendamiento",
    select: ARRENDAMIENTO_COLS,
    datos,
    mensajeVacio: "El alta no devolvió el arrendamiento creado.",
  });
}

export async function actualizarArrendamiento(
  token: string,
  id: string,
  patch: Record<string, unknown>,
): Promise<void> {
  await req(`/rest/v1/arrendamiento?id=eq.${id}`, { method: "PATCH", token, body: patch });
}

/** Cobros de renta de un arrendamiento (aportaciones ligadas), más recientes primero. */
export async function cobrosDeArrendamiento(token: string, arrendamientoId: string): Promise<AportacionRow[]> {
  return json<AportacionRow[]>(
    await req(
      `/rest/v1/aportacion?select=${APORTACION_COLS}&arrendamiento_id=eq.${arrendamientoId}&order=fecha_acto.desc`,
      { token },
    ),
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Proyectos (alta con caracterización sellada)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fila de `proyecto` derivada del esquema real. Drifts corregidos:
 *  - `finalidad`: era `string` a mano; la columna real es el enum
 *    `finalidad_proyecto` ("venta"|"renta"|"mixto"|"uso_propio").
 *  - `otras_empresas`: era `boolean` (no-null) a mano; la columna real es
 *    `boolean | null` (nullable, sin default NOT NULL) — un proyecto puede
 *    no tener esta pregunta contestada todavía.
 * Los numeric (monto_desarrollo, unidades_comercializadas, costo_unidad)
 * siguen el mismo patrón `number | string` que el resto (PostgREST).
 * Nota: la tabla real también tiene `fraccion_aplicable: string[]`, que
 * PROYECTO_COLS no selecciona — no es drift porque no se declara aquí.
 */
export type ProyectoRow = Pick<
  Tables<"proyecto">,
  | "id"
  | "nombre"
  | "es_desarrollo_inmobiliario"
  | "justificacion_sellada"
  | "finalidad"
  | "jurisdiccion"
  | "registro_licencia"
  | "entidad_federativa"
  | "codigo_postal"
  | "colonia"
  | "calle"
  | "tipo_desarrollo"
  | "otras_empresas"
  | "created_at"
> & {
  monto_desarrollo: ColumnaNumericComoTexto | null;
  unidades_comercializadas: ColumnaNumericComoTexto | null;
  costo_unidad: ColumnaNumericComoTexto | null;
};

const PROYECTO_COLS =
  "id,nombre,es_desarrollo_inmobiliario,justificacion_sellada,finalidad,jurisdiccion," +
  "registro_licencia,entidad_federativa,codigo_postal,colonia,calle,tipo_desarrollo," +
  "monto_desarrollo,unidades_comercializadas,costo_unidad,otras_empresas,created_at";

export async function listarProyectosFull(token: string): Promise<ProyectoRow[]> {
  return json<ProyectoRow[]>(
    await req(`/rest/v1/proyecto?select=${PROYECTO_COLS}&order=created_at.desc`, { token }),
  );
}

export async function crearProyecto(
  token: string,
  tenantId: string,
  datos: Record<string, unknown>,
): Promise<ProyectoRow> {
  return crearEntidad<ProyectoRow>({
    token,
    tenantId,
    tabla: "proyecto",
    select: PROYECTO_COLS,
    datos,
    mensajeVacio: "El alta no devolvió el proyecto creado.",
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Pendientes: temporizadores (relojes de ley)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fila de `temporizador` derivada del esquema real. Drift corregido: `tipo`
 * y `estado` eran `string` a mano; las columnas reales son los enums
 * `tipo_temporizador` y `estado_temporizador`.
 * `metadatos` se mantiene `Record<string, unknown>` (no el `Json` generado):
 * el código consumidor (PendientesView.tsx) indexa con literales de string
 * (`r.metadatos["formato_exportacion_id"]`), lo cual `Json` no permite de forma
 * segura porque también admite las variantes primitivas/array de la unión.
 */
export type TemporizadorRow = Pick<
  Tables<"temporizador">,
  "id" | "tipo" | "entidad_ref" | "vence_en" | "estado"
> & {
  metadatos: Record<string, unknown>;
};

export async function listarTemporizadores(token: string): Promise<TemporizadorRow[]> {
  return json<TemporizadorRow[]>(
    await req(
      "/rest/v1/temporizador?select=id,tipo,entidad_ref,vence_en,estado,metadatos&estado=in.(pendiente,escalado,vencido)&order=vence_en.asc",
      { token },
    ),
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Cumplimiento: casos con decisión firmada (0024)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fila de `caso` derivada del esquema real. Drift corregido: `tipo` y
 * `estado` eran `string` a mano; las columnas reales son los enums
 * `tipo_caso` y `estado_caso`.
 */
export type CasoRow = Pick<
  Tables<"caso">,
  | "id"
  | "tipo"
  | "titulo"
  | "descripcion"
  | "severidad"
  | "entidad_ref"
  | "estado"
  | "decision"
  | "fundamento"
  | "decidido_en"
  | "created_at"
>;

export async function listarCasos(token: string): Promise<CasoRow[]> {
  return json<CasoRow[]>(
    await req(
      "/rest/v1/caso?select=id,tipo,titulo,descripcion,severidad,entidad_ref,estado,decision,fundamento,decidido_en,created_at&order=estado.asc,created_at.desc",
      { token },
    ),
  );
}

export async function resolverCaso(token: string, casoId: string, decision: string, fundamento: string): Promise<void> {
  await req("/rest/v1/rpc/resolver_caso", {
    token,
    body: { p_caso: casoId, p_decision: decision, p_fundamento: fundamento },
  });
}

const ESTADOS_CERRADOS_PENDIENTES = new Set(["acuse_capturado", "cancelado"]);

export async function contarPendientes(token: string): Promise<number> {
  const [relojes, casos, informes, personas, exportaciones] = await Promise.all([
    listarTemporizadores(token),
    listarCasos(token),
    listarInformes(token),
    listarPersonas(token),
    listarExportacionesFormato(token),
  ]);
  return casos.filter((c) => c.estado === "abierto").length
    + informes.filter((i) => !ESTADOS_CERRADOS_PENDIENTES.has(i.estado)).length
    + exportaciones.filter((x) => !ESTADOS_CERRADOS_PENDIENTES.has(x.estado)).length
    + relojes.length
    + personas.filter((p) => !p.expediente_completo).length;
}

// ─────────────────────────────────────────────────────────────────────────────
// Configuración: sujeto obligado + e.firma + formatos por fracción
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fila de `sujeto_obligado` derivada del esquema real. Drift corregido:
 * `tipo` era `string` a mano; la columna real es el enum `tipo_sujeto`
 * (pm|pf|fideicomiso|spe).
 */
export type SujetoObligadoRow = Pick<
  Tables<"sujeto_obligado">,
  "id" | "rfc" | "razon_social" | "tipo" | "obligacion_desde" | "obligacion_hasta"
>;

export async function obtenerSujetoObligado(token: string): Promise<SujetoObligadoRow | null> {
  const filas = await json<SujetoObligadoRow[]>(
    await req("/rest/v1/sujeto_obligado?select=id,rfc,razon_social,tipo,obligacion_desde,obligacion_hasta", { token }),
  );
  return filas[0] ?? null;
}

/**
 * Fila de `efirma` derivada del esquema real. Drift corregido: `estado` era
 * `string` a mano; la columna real es el enum `efirma_estado`
 * (vigente|revocada).
 */
export type EfirmaRow = Pick<
  Tables<"efirma">,
  "id" | "numero_serie" | "rfc" | "vigente_desde" | "vigente_hasta" | "estado"
>;

export async function listarEfirmas(token: string): Promise<EfirmaRow[]> {
  return json<EfirmaRow[]>(
    await req("/rest/v1/efirma?select=id,numero_serie,rfc,vigente_desde,vigente_hasta,estado&order=vigente_hasta.desc", { token }),
  );
}

export async function crearEfirma(token: string, tenantId: string, datos: Record<string, unknown>): Promise<void> {
  await req("/rest/v1/efirma", { token, body: { tenant_id: tenantId, ...datos } });
}

/** ¿Qué formato oficial SAT/SPPLD existe para una fracción? (NULL = sin publicar). */
export async function formatoOficial(token: string, fraccion: string): Promise<string | null> {
  const hoy = new Date().toISOString().slice(0, "YYYY-MM-DD".length);
  const res = await req("/rest/v1/rpc/formato_oficial_para", {
    token,
    body: { p_fraccion: fraccion, p_fecha: hoy },
  });
  const data: unknown = await res.json();
  return typeof data === "string" ? data : null;
}

/** ¿Qué formato puede generar Argus hoy para una fracción? (NULL = adaptador pendiente). */
export async function formatoVigente(token: string, fraccion: string): Promise<string | null> {
  const hoy = new Date().toISOString().slice(0, "YYYY-MM-DD".length);
  const res = await req("/rest/v1/rpc/formato_vigente_para", {
    token,
    body: { p_fraccion: fraccion, p_fecha: hoy },
  });
  const data: unknown = await res.json();
  return typeof data === "string" ? data : null;
}

// ─────────────────────────────────────────────────────────────────────────────
// F1 (OT-5): alta de padrón SPPLD por fracción — advertir al generar, bloquear
// al presentar (presentar_informe/presentar_formato_exportacion lo exigen).
// ─────────────────────────────────────────────────────────────────────────────

/** Fila de `alta_padron` derivada del esquema real. Sin drift de tipos. */
export type AltaPadronRow = Pick<
  Tables<"alta_padron">,
  "id" | "fraccion" | "fecha_alta" | "acuse_documento_id" | "confirmada"
>;

export async function listarAltasPadron(token: string): Promise<AltaPadronRow[]> {
  return json<AltaPadronRow[]>(
    await req(
      "/rest/v1/alta_padron?select=id,fraccion,fecha_alta,acuse_documento_id,confirmada&order=fraccion.asc",
      { token },
    ),
  );
}

export async function registrarAltaPadron(
  token: string,
  fraccion: string,
  fechaAlta: string,
  confirmada: boolean,
  acuseDocumentoId: string | null = null,
): Promise<void> {
  await req("/rest/v1/rpc/registrar_alta_padron", {
    token,
    body: {
      p_fraccion: fraccion,
      p_fecha_alta: fechaAlta,
      p_confirmada: confirmada,
      p_acuse_documento_id: acuseDocumentoId,
    },
  });
}
